/**
 * Hook that polls WebRTC stats from the SDK every second.
 *
 * Client-side stats (from RTCStatsReport):
 *   - Bitrate: delta bytesReceived / delta time
 *   - FPS: framesPerSecond from inbound-rtp
 *   - Ping: currentRoundTripTime from candidate-pair
 *   - Jitter: jitter from inbound-rtp
 */

import { useState, useEffect, useRef } from "react";
import type { GameLiftStreams } from "../lib/gamelift-streams-websdk/gameliftstreams-1.1.0.mjs";

export interface StreamStats {
  bitrateMbps: number;
  fps: number;
  pingMs: number;
  jitterMs: number;
}

export function useStreamStats(glsInstance: GameLiftStreams | null): StreamStats | null {
  const [stats, setStats] = useState<StreamStats | null>(null);
  const prevRef = useRef<{ bytes: number; time: number } | null>(null);

  useEffect(() => {
    if (!glsInstance) return;

    const interval = setInterval(async () => {
      try {
        const report = await glsInstance.getVideoRTCStats();

        let bitrateMbps = 0;
        let fps = 0;
        let pingMs = 0;
        let jitterMs = 0;

        report.forEach((entry: any) => {
          if (entry.type === "inbound-rtp" && entry.kind === "video") {
            fps = entry.framesPerSecond ?? 0;
            jitterMs = (entry.jitter ?? 0) * 1000;

            const now = performance.now();
            const bytes = entry.bytesReceived ?? 0;
            if (prevRef.current) {
              const dt = (now - prevRef.current.time) / 1000;
              const db = bytes - prevRef.current.bytes;
              if (dt > 0) {
                bitrateMbps = (db * 8) / dt / 1_000_000;
              }
            }
            prevRef.current = { bytes, time: now };
          }

          if (entry.type === "candidate-pair" && entry.state === "succeeded") {
            pingMs = (entry.currentRoundTripTime ?? 0) * 1000;
          }
        });

        setStats({ bitrateMbps, fps, pingMs, jitterMs });

        // Log mic/audio stats for latency debugging
        const micReport = await glsInstance.getMicrophoneRTCStats();
        const micStats: Record<string, any> = {};
        micReport.forEach((entry: any) => {
          if (entry.type === "outbound-rtp" && entry.kind === "audio") {
            micStats.outbound = {
              packetsSent: entry.packetsSent,
              bytesSent: entry.bytesSent,
              targetBitrate: entry.targetBitrate,
              avgSendDelayMs: entry.packetsSent > 0
                ? ((entry.totalPacketSendDelay ?? 0) / entry.packetsSent * 1000).toFixed(1)
                : 0,
              retransmittedPackets: entry.retransmittedPacketsSent,
              nackCount: entry.nackCount,
            };
          }
          if (entry.type === "remote-inbound-rtp" && entry.kind === "audio") {
            micStats.remote = {
              rttMs: ((entry.roundTripTime ?? 0) * 1000).toFixed(1),
              jitterMs: ((entry.jitter ?? 0) * 1000).toFixed(1),
              packetsLost: entry.packetsLost,
              fractionLost: entry.fractionLost?.toFixed(3),
            };
          }
          if (entry.type === "media-source" && entry.kind === "audio") {
            micStats.source = {
              audioLevel: entry.audioLevel?.toFixed(4),
              totalAudioEnergy: entry.totalAudioEnergy?.toFixed(4),
              echoReturnLoss: entry.echoReturnLoss?.toFixed(1),
              echoReturnLossEnhancement: entry.echoReturnLossEnhancement?.toFixed(1),
            };
          }
        });
        // if (Object.keys(micStats).length > 0) {
          // console.log("[mic stats]", micStats);
        // }
      } catch (err) {
        console.error("Failed to read stats:", err);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [glsInstance]);

  return stats;
}
